import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Col, Row } from 'antd';
import { Container, FormFieldHeading } from '@/components/Elements';
import { purchaseDetailLabelMapper } from '../schema/purchaseDetailSchema';
import { useCountryOptions } from '@/hooks/useCountryOptions';

import {
  RadioGroupField,
  InputField,
  DatePickerField,
  SelectField,
} from '@/components/Form';

import {
  PROPERTY_TYPE_OPTIONS,
  PURCHASING_UNDER_OPTIONS,
} from '../constant/optionList';

import useUnsavedChangesWarning from '@/hooks/useUnsavedChangesWarning';

import Man from '@/assets/images/icons/man.svg';
import JointProperty from '@/assets/images/icons/property-joint.svg';
import Trust from '@/assets/images/icons/trust.svg';
import NonTrading from '@/assets/images/icons/property-non-trading.svg';
import House from '@/assets/images/icons/house.svg';
import Building from '@/assets/images/icons/building.svg';
import Apartment from '@/assets/images/icons/apartment.svg';
import OffApartment from '@/assets/images/icons/offAppartment.svg';
import Map from '@/assets/images/icons/map.svg';
import SMSF from '@/assets/images/icons/property-non-trading.svg';
import useScrollToTop from '@/hooks/useScrollTop';
import FormMainLabel from '@/components/Elements/Heading/FormMainLabel';
import FormSubLabel from '@/components/Elements/Heading/FormSubLabel';
import {
  AGREE_OPTIONS,
  CITIZENSHIP_OPTIONS,
  FREQUENCY_OPTIONS,
  RELATIONSHIP_OPTIONS,
  RESIDENTIAL_STATUS_OPTIONS,
} from '@/constant/options';
import dayjs from 'dayjs';
import PhoneInputField from '@/components/Form/PhoneInputField';

const iconMapper = {
  man: Man,
  jointProperty: JointProperty,
  trust: Trust,
  nonTrading: NonTrading,
  house: House,
  building: Building,
  apartment: Apartment,
  offApartment: OffApartment,
  map: Map,
  smsf: SMSF,
};

interface Props {
  data: any;
  setValue: any;
  watch: any;
  control: any;
  errors: any;
  register: any;
  unregister: any;
  resetField: any;
}

const NewPurchaseDetail: React.FC<Props> = ({
  data: purchaseDetailData,
  setValue,
  watch,
  control,
  errors,
  register,
  unregister,
  resetField,
}) => {
  useUnsavedChangesWarning(true);
  useScrollToTop();
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const countryOptions = useCountryOptions();

  console.log(
    'purchaseDetailData',
    purchaseDetailData?.coapplicantFirstName,
    purchaseDetailData?.coapplicantLastName,
    purchaseDetailData?.purchasingUnder,
  );
  const watchPurchasingUnder = watch('purchasingUnder');
  const watchCoapplicantCitizenship = watch('coapplicantCitizenship');
  const watchCoapplicantVisaSubClass = watch('coapplicantVisaSubClass');
  const watchCoapplicantVisaExpiryDate = watch('coapplicantVisaExpiryDate');
  const watchCoapplicantLivingTogether = watch('coapplicantLivingTogether');
  const watchCoapplicantCurrentResidentialStatus = watch(
    'coapplicantCurrentResidentialStatus',
  );
  const watchCoapplicantCountry = watch('coapplicantCountry');
  useEffect(() => {
    if (purchaseDetailData) {
      setValue('purchasingUnder', purchaseDetailData.purchasingUnder);
      setValue('propertyType', purchaseDetailData.propertyType);
      setValue('propertyPurpose', purchaseDetailData.propertyPurpose);
      setValue('propertyTimeframe', purchaseDetailData.propertyTimeframe);
      setValue('coapplicantFirstName', purchaseDetailData.coapplicantFirstName);
      setValue('coapplicantLastName', purchaseDetailData.coapplicantLastName);
      setValue('coapplicantEmail', purchaseDetailData.coapplicantEmail);
      setValue('coapplicantPhone', purchaseDetailData.coapplicantPhone);
      setValue(
        'coapplicantRelationship',
        purchaseDetailData.coapplicantRelationship,
      );
      setValue(
        'coapplicantCitizenship',
        purchaseDetailData.coapplicantCitizenship,
      );
      setValue(
        'coapplicantVisaSubClass',
        purchaseDetailData.coapplicantVisaSubClass,
      );
      setValue(
        'coapplicantVisaExpiryDate',
        purchaseDetailData.coapplicantVisaExpiryDate,
      );
      setValue(
        'coapplicantLivingTogether',
        purchaseDetailData.coapplicantLivingTogether,
      );
      setValue('coapplicantCountry', purchaseDetailData.coapplicantCountry);
      setValue(
        'coapplicantPermanentResident',
        purchaseDetailData.coapplicantPermanentResident,
      );
      setValue(
        'coapplicantCurrentResidentialStatus',
        purchaseDetailData.coapplicantCurrentResidentialStatus,
      );
      setValue(
        'coapplicantRentExpense',
        purchaseDetailData.coapplicantRentExpense,
      );
      setValue(
        'coapplicantFrequencyOfRent',
        purchaseDetailData.coapplicantFrequencyOfRent,
      );
      setValue(
        'coapplicantIsTheResidentialPlaceMortgaged',
        purchaseDetailData.coapplicantIsTheResidentialPlaceMortgaged,
      );
      setValue(
        'coapplicantResidentalOtherSpecification',
        purchaseDetailData.coapplicantResidentalOtherSpecification,
      );
    }
  }, [purchaseDetailData]);

  useEffect(() => {
    if (watchPurchasingUnder === 'solo_borrower') {
      console.log('watchPurchasingUnder', watchPurchasingUnder);
      [
        'coapplicantFirstName',
        'coapplicantLastName',
        'coapplicantEmail',
        'coapplicantPhone',
        'coapplicantRelationship',
        'coapplicantCitizenship',
        'coapplicantVisaSubClass',
        'coapplicantVisaExpiryDate',
        'coapplicantLivingTogether',
        'coapplicantCountry',
        'coapplicantPermanentResident',
        'coapplicantCurrentResidentialStatus',
        'coapplicantRentExpense',
        'coapplicantFrequencyOfRent',
        'coapplicantIsTheResidentialPlaceMortgaged',
        'coapplicantResidentalOtherSpecification',
      ].forEach((field) => {
        resetField(field);
      });
    }
  }, [watchPurchasingUnder, resetField]);

  useEffect(() => {
    if (
      watchCoapplicantCitizenship === 'foreign_national' ||
      watchCoapplicantCitizenship === 'newzeland_citizen' ||
      watchCoapplicantCitizenship === 'australian_citizen'
    ) {
      resetField('coapplicantVisaSubClass');
      resetField('coapplicantVisaExpiryDate');
    }
  }, [watchCoapplicantCitizenship, resetField]);

  useEffect(() => {
    if (watchCoapplicantLivingTogether === true) {
      resetField('coapplicantCountry');
      resetField('coapplicantPermanentResident');
      resetField('coapplicantCurrentResidentialStatus');
      resetField('coapplicantRentExpense');
      resetField('coapplicantFrequencyOfRent');
      resetField('coapplicantIsTheResidentialPlaceMortgaged');
      resetField('coapplicantResidentalOtherSpecification');
    }
  }, [watchCoapplicantLivingTogether, resetField]);

  useEffect(() => {
    if (watchCoapplicantCurrentResidentialStatus === 'renting') {
      resetField('coapplicantResidentalOtherSpecification');
      resetField('coapplicantIsTheResidentialPlaceMortgaged');
    }
    if (watchCoapplicantCurrentResidentialStatus === 'live_own_place') {
      resetField('coapplicantRentExpense');
      resetField('coapplicantFrequencyOfRent');
      resetField('coapplicantResidentalOtherSpecification');
    }
    if (
      watchCoapplicantCurrentResidentialStatus ===
        'rent_free_or_boarding_parent' ||
      watchCoapplicantCurrentResidentialStatus ===
        'rent_free_or_boarding_spouse' ||
      watchCoapplicantCurrentResidentialStatus ===
        'rent_free_or_boarding_friend' ||
      watchCoapplicantCurrentResidentialStatus ===
        'rent_free_or_boarding_employer'
    ) {
      resetField('coapplicantRentExpense');
      resetField('coapplicantFrequencyOfRent');
      resetField('coapplicantResidentalOtherSpecification');
      resetField('coapplicantIsTheResidentialPlaceMortgaged');
    }
    if (watchCoapplicantCurrentResidentialStatus === 'other') {
      resetField('coapplicantRentExpense');
      resetField('coapplicantFrequencyOfRent');
      resetField('coapplicantIsTheResidentialPlaceMortgaged');
    }
  }, [watchCoapplicantCurrentResidentialStatus, resetField]);

  return (
    <Container width="md">
      <Row>
        <Col xs={24}>
          <Row gutter={30}>
            <Col xs={24}>
              <div
                className="form-section-header form-control"
                id="purchasingUnder"
              >
                <FormMainLabel
                  index={1}
                  label={purchaseDetailLabelMapper['purchasingUnder']}
                />
                <RadioGroupField
                  size="large"
                  name="purchasingUnder"
                  control={control}
                  iconMapper={iconMapper}
                  options={PURCHASING_UNDER_OPTIONS}
                  error={errors.purchasingUnder?.message}
                  formDirection="column"
                  cols={{ sm: 1, md: 1, lg: 2 }}
                />
              </div>
            </Col>
            {(watchPurchasingUnder === 'trust' ||
              watchPurchasingUnder === 'joint_borrower' ||
              watchPurchasingUnder === 'non_trading_company') && (
              <Col xs={24}>
                <Card
                  title="Coapplicant Details"
                  className="form-section coapplicant-section"
                >
                  <Row gutter={30}>
                    <Col xs={24} md={12}>
                      <InputField
                        label="Coapplicant First Name"
                        name="coapplicantFirstName"
                        control={control}
                        error={errors?.coapplicantFirstName?.message ?? ''}
                        placeholder="Enter coapplicant first name"
                        size="large"
                      />
                    </Col>
                    <Col xs={24} md={12}>
                      <InputField
                        label="Coapplicant Last Name"
                        name="coapplicantLastName"
                        control={control}
                        error={errors?.coapplicantLastName?.message ?? ''}
                        placeholder="Enter coapplicant last name"
                        size="large"
                      />
                    </Col>
                    <Col xs={24} md={12}>
                      <InputField
                        label="Coapplicant Email"
                        name="coapplicantEmail"
                        control={control}
                        error={errors?.coapplicantEmail?.message ?? ''}
                        placeholder="Enter coapplicant email"
                        size="large"
                      />
                    </Col>
                    <Col xs={24} md={12}>
                      <PhoneInputField
                        label="Coapplicant Phone"
                        name="coapplicantPhone"
                        control={control}
                        error={errors?.coapplicantPhone?.message ?? ''}
                      />
                    </Col>
                    <Col xs={24}>
                      <RadioGroupField
                        label="Your relationship to this person"
                        size="large"
                        name="coapplicantRelationship"
                        options={RELATIONSHIP_OPTIONS}
                        control={control}
                        error={errors?.coapplicantRelationship?.message ?? ''}
                      />
                    </Col>
                    <Col xs={24}>
                      <RadioGroupField
                        label="Coapplicant Citizenship"
                        size="large"
                        name="coapplicantCitizenship"
                        options={CITIZENSHIP_OPTIONS}
                        control={control}
                        error={errors?.coapplicantCitizenship?.message ?? ''}
                      />
                    </Col>

                    {(watchCoapplicantCitizenship === 'australian_pr_visa' ||
                      watchCoapplicantCitizenship === 'australian_tr_visa') && (
                      <>
                        <Col xs={24} md={12}>
                          <InputField
                            label="Visa Sub-Class"
                            name="coapplicantVisaSubClass"
                            control={control}
                            error={
                              errors.coapplicantVisaSubClass?.message ?? ''
                            }
                            placeholder="Enter your visa subclass"
                            size="large"
                          />
                        </Col>
                        <Col xs={24} md={12}>
                          <DatePickerField
                            label="Visa Expiry Date"
                            name="coapplicantVisaExpiryDate"
                            control={control}
                            error={
                              errors.coapplicantVisaExpiryDate?.message ?? ''
                            }
                            size="large"
                            placeholder="Select Visa Expiry Date"
                            minDate={dayjs()}
                          />
                        </Col>
                      </>
                    )}
                    <Col xs={24}>
                      <RadioGroupField
                        label="Is this person living together with you?"
                        size="large"
                        name="coapplicantLivingTogether"
                        options={AGREE_OPTIONS}
                        control={control}
                        error={errors?.coapplicantLivingTogether?.message ?? ''}
                      />
                    </Col>
                    <Col xs={24}>
                      {watchCoapplicantLivingTogether === false && (
                        <Row gutter={[30, 10]}>
                          <Col xs={24}>
                            <SelectField
                              label="Coapplicant Country"
                              showSearch={true}
                              options={countryOptions}
                              name="coapplicantCountry"
                              filterName="country"
                              control={control}
                              size="large"
                              placeholder="Select your country"
                              error={errors?.coapplicantCountry?.message ?? ''}
                            />
                          </Col>
                          {watchCoapplicantCountry === 'SG' && (
                            <Col xs={24} md={12}>
                              <SelectField
                                placeholder="Permanent Resident?"
                                label="Are they a Permanent Resident (PR) of this country?"
                                name="coapplicantPermanentResident"
                                control={control}
                                options={AGREE_OPTIONS}
                                error={
                                  errors?.coapplicantPermanentResident
                                    ?.message ?? ''
                                }
                                size="large"
                              />
                            </Col>
                          )}
                          <Col xs={24}>
                            <RadioGroupField
                              label="Coapplicant current Residential Status"
                              name="coapplicantCurrentResidentialStatus"
                              control={control}
                              error={
                                errors.coapplicantCurrentResidentalStatus
                                  ?.message
                              }
                              options={RESIDENTIAL_STATUS_OPTIONS}
                              cols={{ sm: 1, md: 1, lg: 2 }}
                              size="large"
                            />
                          </Col>
                          <Col xs={24}>
                            <Row gutter={[30, 30]} id="residentalDetails">
                              {watchCoapplicantCurrentResidentialStatus ===
                                'renting' && (
                                <>
                                  <Col xs={24} md={12}>
                                    <InputField
                                      label="Rent Expense (In the currency of your country of residence)"
                                      type="number"
                                      name="coapplicantRentExpense"
                                      control={control}
                                      error={
                                        errors.coapplicantRentExpense
                                          ?.message ?? ''
                                      }
                                      placeholder="Enter the rent expense of your country of residence"
                                      size="large"
                                    />
                                  </Col>
                                  <Col xs={24} md={12}>
                                    <SelectField
                                      label="Frequency of Rent"
                                      options={FREQUENCY_OPTIONS}
                                      name="coapplicantFrequencyOfRent"
                                      filterName="country"
                                      control={control}
                                      size="large"
                                      placeholder="Select your country"
                                      error={
                                        errors.coapplicantFrequencyOfRent
                                          ?.message ?? ''
                                      }
                                    />
                                  </Col>
                                </>
                              )}

                              {watchCoapplicantCurrentResidentialStatus ===
                                'live_own_place' && (
                                <Col xs={24}>
                                  <SelectField
                                    options={AGREE_OPTIONS}
                                    label="Is This Place Mortgaged?"
                                    name="coapplicantIsTheResidentialPlaceMortgaged"
                                    control={control}
                                    error={
                                      errors
                                        .coapplicantIsTheResidentialPlaceMortgaged
                                        ?.message ?? ''
                                    }
                                    placeholder="Is this place mortgaged?"
                                    size="large"
                                  />
                                </Col>
                              )}

                              {watchCoapplicantCurrentResidentialStatus ===
                                'other' && (
                                <Col xs={24}>
                                  <InputField
                                    label="Provide Further Specification"
                                    name="coapplicantResidentalOtherSpecification"
                                    control={control}
                                    error={
                                      errors
                                        .coapplicantResidentalOtherSpecification
                                        ?.message ?? ''
                                    }
                                    placeholder="Enter further specification"
                                    size="large"
                                  />
                                </Col>
                              )}
                            </Row>
                          </Col>
                        </Row>
                      )}
                    </Col>
                  </Row>
                </Card>
              </Col>
            )}

            <Col xs={24} md={24}>
              <div
                className="form-section-header form-control"
                id="propertyType"
              >
                <FormMainLabel
                  index={2}
                  label={purchaseDetailLabelMapper['propertyType']}
                />
                <RadioGroupField
                  size="large"
                  name="propertyType"
                  control={control}
                  iconMapper={iconMapper}
                  options={PROPERTY_TYPE_OPTIONS}
                  error={errors.propertyType?.message}
                  cols={{ sm: 1, md: 1, lg: 2 }}
                />
              </div>
            </Col>
            <Col xs={24}>
              <Button
                ref={submitButtonRef}
                type="primary"
                htmlType="submit"
                style={{ display: 'none' }}
              >
                Submit
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
    </Container>
  );
};

export default NewPurchaseDetail;
